import * as THREE from 'three';
import { FikoWebGPU } from './FikoWebGPU';

/**
 * FikoGPUBVH - GPU-Optimized Bounding Volume Hierarchy
 *
 * STACKLESS GPU TRAVERSAL - Zero stack usage, pure iterative
 *
 * Key Features:
 * - SAH (Surface Area Heuristic) for optimal tree construction
 * - Stackless traversal using skip pointers
 * - Depth-first memory layout for cache efficiency
 * - Lazy rebuild (only affected subtrees)
 * - GPU buffer upload for WebGPU compute shaders
 *
 * Memory Layout per Node (64 bytes aligned):
 * - AABB min (12 bytes) + padding (4 bytes)
 * - AABB max (12 bytes) + padding (4 bytes)
 * - leftChild (4 bytes), rightChild (4 bytes), skipPointer (4 bytes), firstTriangle (4 bytes)
 * - triangleCount (4 bytes), flags (4 bytes), objectId (4 bytes), padding (4 bytes)
 *
 * Performance Target: O(log n) traversal, 100x faster than brute force
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface GPUBVHNode {
    // AABB bounds (24 bytes + 8 padding = 32 bytes)
    minX: number; minY: number; minZ: number; pad0: number;
    maxX: number; maxY: number; maxZ: number; pad1: number;

    // Tree structure (16 bytes)
    leftChild: number;      // Index of left child (0 = leaf)
    rightChild: number;     // Index of right child (0 = leaf)
    skipPointer: number;    // Next node if ray misses this AABB
    firstTriangle: number;  // First triangle index (for leaves)

    // Leaf data (16 bytes)
    triangleCount: number;  // Number of triangles in leaf
    flags: number;          // bit 0 = isLeaf
    objectId: number;       // Object ID for picking
    pad2: number;
}

export interface GPUTriangle {
    // Vertex 0 (16 bytes)
    v0x: number; v0y: number; v0z: number; pad0: number;
    // Vertex 1 (16 bytes)
    v1x: number; v1y: number; v1z: number; pad1: number;
    // Vertex 2 (16 bytes)
    v2x: number; v2y: number; v2z: number; pad2: number;
    // Metadata (16 bytes)
    objectId: number;
    triangleId: number;
    materialId: number;
    pad3: number;
}

interface BuildTriangle {
    v0: THREE.Vector3;
    v1: THREE.Vector3;
    v2: THREE.Vector3;
    centroid: THREE.Vector3;
    objectId: number;
    triangleId: number;
    materialId: number;
    originalIndex: number;
}

interface AABB {
    min: THREE.Vector3;
    max: THREE.Vector3;
}

// ============================================================================
// WGSL SHADERS
// ============================================================================

// Stackless BVH Traversal Shader
export const WGSL_BVH_TRAVERSAL = `
// Stackless BVH traversal for GPU ray-triangle intersection
// Uses skip pointers for O(log n) traversal without stack

struct Ray {
    origin: vec3f,
    direction: vec3f,
    tMin: f32,
    tMax: f32
}

struct BVHNode {
    minBounds: vec3f,
    pad0: f32,
    maxBounds: vec3f,
    pad1: f32,
    leftChild: u32,
    rightChild: u32,
    skipPointer: u32,
    firstTriangle: u32,
    triangleCount: u32,
    flags: u32,
    objectId: u32,
    pad2: u32
}

struct Triangle {
    v0: vec3f,
    pad0: f32,
    v1: vec3f,
    pad1: f32,
    v2: vec3f,
    pad2: f32,
    objectId: u32,
    triangleId: u32,
    materialId: u32,
    pad3: u32
}

struct HitResult {
    hit: u32,
    objectId: u32,
    triangleId: u32,
    distance: f32,
    hitPointX: f32,
    hitPointY: f32,
    hitPointZ: f32,
    normalX: f32,
    normalY: f32,
    normalZ: f32,
    u: f32,
    v: f32
}

struct Uniforms {
    rayOriginX: f32,
    rayOriginY: f32,
    rayOriginZ: f32,
    rayDirX: f32,
    rayDirY: f32,
    rayDirZ: f32,
    tMin: f32,
    tMax: f32,
    nodeCount: u32,
    triangleCount: u32,
    pad0: u32,
    pad1: u32
}

@group(0) @binding(0) var<storage, read> bvhNodes: array<BVHNode>;
@group(0) @binding(1) var<storage, read> triangles: array<Triangle>;
@group(0) @binding(2) var<storage, read_write> hitResult: HitResult;
@group(0) @binding(3) var<uniform> uniforms: Uniforms;

const EPSILON: f32 = 0.00001;

// Fast Ray-AABB intersection (slab method)
fn rayBoxIntersect(
    rayOrigin: vec3f,
    invDir: vec3f,
    minB: vec3f,
    maxB: vec3f,
    closestT: f32
) -> bool {
    let t1 = (minB - rayOrigin) * invDir;
    let t2 = (maxB - rayOrigin) * invDir;

    let tmin = max(max(min(t1.x, t2.x), min(t1.y, t2.y)), min(t1.z, t2.z));
    let tmax = min(min(max(t1.x, t2.x), max(t1.y, t2.y)), max(t1.z, t2.z));

    return tmax >= max(tmin, 0.0) && tmin < closestT;
}

// Moller-Trumbore ray-triangle intersection
fn rayTriangleIntersect(
    rayOrigin: vec3f,
    rayDir: vec3f,
    v0: vec3f,
    v1: vec3f,
    v2: vec3f,
    closestT: f32
) -> vec4f {
    // Returns (t, u, v, hit) where hit = 1.0 if intersection found

    let edge1 = v1 - v0;
    let edge2 = v2 - v0;
    let h = cross(rayDir, edge2);
    let a = dot(edge1, h);

    if (abs(a) < EPSILON) {
        return vec4f(-1.0, 0.0, 0.0, 0.0);
    }

    let f = 1.0 / a;
    let s = rayOrigin - v0;
    let u = f * dot(s, h);

    if (u < 0.0 || u > 1.0) {
        return vec4f(-1.0, 0.0, 0.0, 0.0);
    }

    let q = cross(s, edge1);
    let v = f * dot(rayDir, q);

    if (v < 0.0 || u + v > 1.0) {
        return vec4f(-1.0, 0.0, 0.0, 0.0);
    }

    let t = f * dot(edge2, q);

    if (t > EPSILON && t < closestT) {
        return vec4f(t, u, v, 1.0);
    }

    return vec4f(-1.0, 0.0, 0.0, 0.0);
}

@compute @workgroup_size(1)
fn main() {
    let rayOrigin = vec3f(uniforms.rayOriginX, uniforms.rayOriginY, uniforms.rayOriginZ);
    let rayDir = vec3f(uniforms.rayDirX, uniforms.rayDirY, uniforms.rayDirZ);
    let invDir = 1.0 / rayDir;

    var closestT = uniforms.tMax;
    var bestHit = HitResult(0u, 0u, 0u, 999999.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0);

    var currentNode: u32 = 0u;
    let maxIterations = uniforms.nodeCount * 2u; // Safety limit
    var iterations: u32 = 0u;

    // Stackless traversal using skip pointers
    while (currentNode < uniforms.nodeCount && iterations < maxIterations) {
        iterations++;
        let node = bvhNodes[currentNode];

        if (rayBoxIntersect(rayOrigin, invDir, node.minBounds, node.maxBounds, closestT)) {
            // Hit AABB
            let isLeaf = (node.flags & 1u) == 1u;

            if (isLeaf) {
                // Test all triangles in leaf
                for (var i: u32 = 0u; i < node.triangleCount; i++) {
                    let triIdx = node.firstTriangle + i;
                    if (triIdx >= uniforms.triangleCount) {
                        break;
                    }

                    let tri = triangles[triIdx];
                    let v0 = vec3f(tri.v0.x, tri.v0.y, tri.v0.z);
                    let v1 = vec3f(tri.v1.x, tri.v1.y, tri.v1.z);
                    let v2 = vec3f(tri.v2.x, tri.v2.y, tri.v2.z);

                    let result = rayTriangleIntersect(rayOrigin, rayDir, v0, v1, v2, closestT);

                    if (result.w > 0.5) {
                        closestT = result.x;
                        let hitPoint = rayOrigin + closestT * rayDir;
                        let edge1 = v1 - v0;
                        let edge2 = v2 - v0;
                        let normal = normalize(cross(edge1, edge2));

                        bestHit.hit = 1u;
                        bestHit.objectId = tri.objectId;
                        bestHit.triangleId = tri.triangleId;
                        bestHit.distance = closestT;
                        bestHit.hitPointX = hitPoint.x;
                        bestHit.hitPointY = hitPoint.y;
                        bestHit.hitPointZ = hitPoint.z;
                        bestHit.normalX = normal.x;
                        bestHit.normalY = normal.y;
                        bestHit.normalZ = normal.z;
                        bestHit.u = result.y;
                        bestHit.v = result.z;
                    }
                }
                // Move to skip pointer after processing leaf
                currentNode = node.skipPointer;
            } else {
                // Interior node - go to left child first
                currentNode = node.leftChild;
            }
        } else {
            // Miss AABB - skip entire subtree
            currentNode = node.skipPointer;
        }
    }

    hitResult = bestHit;
}
`;

// ============================================================================
// MAIN CLASS
// ============================================================================

export class FikoGPUBVH {
    // Tree structure
    private nodes: GPUBVHNode[] = [];
    private triangles: GPUTriangle[] = [];
    private triangleIndices: number[] = [];

    // GPU buffers
    private nodeBuffer: GPUBuffer | null = null;
    private triangleBuffer: GPUBuffer | null = null;
    private webGPU: FikoWebGPU | null = null;

    // Build parameters
    private maxLeafTriangles: number = 4;
    private sahBinCount: number = 12;

    // Object tracking for lazy rebuild
    private objectToTriangleRange: Map<number, { start: number; count: number }> = new Map();
    private dirtyObjects: Set<number> = new Set();

    // Stats
    private stats = {
        nodeCount: 0,
        triangleCount: 0,
        maxDepth: 0,
        leafCount: 0,
        buildTimeMs: 0,
        uploadTimeMs: 0
    };

    constructor() {
        this.initWebGPU();
    }

    private async initWebGPU(): Promise<void> {
        try {
            this.webGPU = await FikoWebGPU.initialize();
        } catch (e) {
            this.webGPU = null;
        }
    }

    // ============================================================================
    // PUBLIC API
    // ============================================================================

    /**
     * Build BVH from Three.js scene objects
     */
    build(objects: THREE.Object3D[]): void {
        const startTime = performance.now();

        // Extract all triangles from meshes
        const buildTriangles: BuildTriangle[] = [];
        let triangleId = 0;

        objects.forEach((obj, objectId) => {
            if (obj instanceof THREE.Mesh && obj.geometry) {
                const startTriangle = buildTriangles.length;
                this.extractTriangles(obj, objectId, buildTriangles, triangleId);
                const count = buildTriangles.length - startTriangle;

                this.objectToTriangleRange.set(objectId, {
                    start: startTriangle,
                    count: count
                });

                triangleId += count;
            }
        });

        if (buildTriangles.length === 0) {
            return;
        }

        // Build tree using SAH
        this.nodes = [];
        this.triangleIndices = [];
        const rootAABB = this.computeAABB(buildTriangles);
        this.buildRecursive(buildTriangles, rootAABB, 0, 0);

        // Compute skip pointers (post-order traversal)
        this.computeSkipPointers();

        // Pack triangles in BVH order
        this.packTriangles(buildTriangles);

        // Update stats
        this.stats.nodeCount = this.nodes.length;
        this.stats.triangleCount = this.triangles.length;
        this.stats.buildTimeMs = performance.now() - startTime;
    }

    /**
     * Upload BVH to GPU
     */
    async uploadToGPU(): Promise<boolean> {
        if (!this.webGPU || !this.webGPU.available) {
            return false;
        }

        const startTime = performance.now();
        const device = this.webGPU.gpuDevice;
        if (!device) return false;

        // Pack nodes into Float32Array
        const nodeData = new Float32Array(this.nodes.length * 16); // 64 bytes / 4 = 16 floats per node

        for (let i = 0; i < this.nodes.length; i++) {
            const n = this.nodes[i];
            const offset = i * 16;

            // AABB min + padding
            nodeData[offset + 0] = n.minX;
            nodeData[offset + 1] = n.minY;
            nodeData[offset + 2] = n.minZ;
            nodeData[offset + 3] = 0; // padding

            // AABB max + padding
            nodeData[offset + 4] = n.maxX;
            nodeData[offset + 5] = n.maxY;
            nodeData[offset + 6] = n.maxZ;
            nodeData[offset + 7] = 0; // padding

            // Tree structure (as floats, will be reinterpreted as u32 in shader)
            const view = new DataView(nodeData.buffer);
            view.setUint32((offset + 8) * 4, n.leftChild, true);
            view.setUint32((offset + 9) * 4, n.rightChild, true);
            view.setUint32((offset + 10) * 4, n.skipPointer, true);
            view.setUint32((offset + 11) * 4, n.firstTriangle, true);

            // Leaf data
            view.setUint32((offset + 12) * 4, n.triangleCount, true);
            view.setUint32((offset + 13) * 4, n.flags, true);
            view.setUint32((offset + 14) * 4, n.objectId, true);
            view.setUint32((offset + 15) * 4, 0, true); // padding
        }

        // Pack triangles into Float32Array
        const triData = new Float32Array(this.triangles.length * 16); // 64 bytes / 4 = 16 floats per triangle

        for (let i = 0; i < this.triangles.length; i++) {
            const t = this.triangles[i];
            const offset = i * 16;

            // Vertex 0
            triData[offset + 0] = t.v0x;
            triData[offset + 1] = t.v0y;
            triData[offset + 2] = t.v0z;
            triData[offset + 3] = 0;

            // Vertex 1
            triData[offset + 4] = t.v1x;
            triData[offset + 5] = t.v1y;
            triData[offset + 6] = t.v1z;
            triData[offset + 7] = 0;

            // Vertex 2
            triData[offset + 8] = t.v2x;
            triData[offset + 9] = t.v2y;
            triData[offset + 10] = t.v2z;
            triData[offset + 11] = 0;

            // Metadata
            const view = new DataView(triData.buffer);
            view.setUint32((offset + 12) * 4, t.objectId, true);
            view.setUint32((offset + 13) * 4, t.triangleId, true);
            view.setUint32((offset + 14) * 4, t.materialId, true);
            view.setUint32((offset + 15) * 4, 0, true);
        }

        // Create GPU buffers
        this.nodeBuffer = device.createBuffer({
            size: nodeData.byteLength,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
            mappedAtCreation: true
        });
        new Float32Array(this.nodeBuffer.getMappedRange()).set(nodeData);
        this.nodeBuffer.unmap();

        this.triangleBuffer = device.createBuffer({
            size: triData.byteLength,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
            mappedAtCreation: true
        });
        new Float32Array(this.triangleBuffer.getMappedRange()).set(triData);
        this.triangleBuffer.unmap();

        this.stats.uploadTimeMs = performance.now() - startTime;
        return true;
    }

    /**
     * Mark object as dirty for lazy rebuild
     */
    markDirty(objectId: number): void {
        this.dirtyObjects.add(objectId);
    }

    /**
     * Perform lazy rebuild of dirty subtrees
     */
    lazyRebuild(objects: THREE.Object3D[]): void {
        if (this.dirtyObjects.size === 0) {
            return;
        }

        // For now, do full rebuild if any objects are dirty
        // TODO: Implement incremental rebuild of affected subtrees only
        this.build(objects);
        this.dirtyObjects.clear();
    }

    /**
     * Get GPU buffers for raycast shader
     */
    getBuffers(): { nodeBuffer: GPUBuffer | null; triangleBuffer: GPUBuffer | null } {
        return {
            nodeBuffer: this.nodeBuffer,
            triangleBuffer: this.triangleBuffer
        };
    }

    /**
     * Get build statistics
     */
    getStats() {
        return { ...this.stats };
    }

    /**
     * Check if BVH is ready for GPU raycast
     */
    isReady(): boolean {
        return this.nodeBuffer !== null && this.triangleBuffer !== null;
    }

    /**
     * Dispose GPU resources
     */
    dispose(): void {
        if (this.nodeBuffer) {
            this.nodeBuffer.destroy();
            this.nodeBuffer = null;
        }
        if (this.triangleBuffer) {
            this.triangleBuffer.destroy();
            this.triangleBuffer = null;
        }
        this.nodes = [];
        this.triangles = [];
    }

    // ============================================================================
    // PRIVATE BUILD METHODS
    // ============================================================================

    /**
     * Extract triangles from mesh geometry
     */
    private extractTriangles(
        mesh: THREE.Mesh,
        objectId: number,
        output: BuildTriangle[],
        startTriangleId: number
    ): void {
        const geometry = mesh.geometry;
        if (!geometry) return;

        const position = geometry.attributes.position;
        if (!position) return;

        const worldMatrix = mesh.matrixWorld;
        const index = geometry.index;

        if (index) {
            // Indexed geometry
            const indices = index.array;
            for (let i = 0; i < indices.length; i += 3) {
                const i0 = indices[i];
                const i1 = indices[i + 1];
                const i2 = indices[i + 2];

                const v0 = new THREE.Vector3(
                    position.getX(i0),
                    position.getY(i0),
                    position.getZ(i0)
                ).applyMatrix4(worldMatrix);

                const v1 = new THREE.Vector3(
                    position.getX(i1),
                    position.getY(i1),
                    position.getZ(i1)
                ).applyMatrix4(worldMatrix);

                const v2 = new THREE.Vector3(
                    position.getX(i2),
                    position.getY(i2),
                    position.getZ(i2)
                ).applyMatrix4(worldMatrix);

                const centroid = new THREE.Vector3()
                    .add(v0).add(v1).add(v2)
                    .divideScalar(3);

                output.push({
                    v0, v1, v2, centroid,
                    objectId,
                    triangleId: startTriangleId + (i / 3),
                    materialId: 0,
                    originalIndex: output.length
                });
            }
        } else {
            // Non-indexed geometry
            const count = position.count;
            for (let i = 0; i < count; i += 3) {
                const v0 = new THREE.Vector3(
                    position.getX(i),
                    position.getY(i),
                    position.getZ(i)
                ).applyMatrix4(worldMatrix);

                const v1 = new THREE.Vector3(
                    position.getX(i + 1),
                    position.getY(i + 1),
                    position.getZ(i + 1)
                ).applyMatrix4(worldMatrix);

                const v2 = new THREE.Vector3(
                    position.getX(i + 2),
                    position.getY(i + 2),
                    position.getZ(i + 2)
                ).applyMatrix4(worldMatrix);

                const centroid = new THREE.Vector3()
                    .add(v0).add(v1).add(v2)
                    .divideScalar(3);

                output.push({
                    v0, v1, v2, centroid,
                    objectId,
                    triangleId: startTriangleId + (i / 3),
                    materialId: 0,
                    originalIndex: output.length
                });
            }
        }
    }

    /**
     * Compute AABB for triangle set
     */
    private computeAABB(triangles: BuildTriangle[]): AABB {
        const min = new THREE.Vector3(Infinity, Infinity, Infinity);
        const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

        for (const tri of triangles) {
            min.min(tri.v0).min(tri.v1).min(tri.v2);
            max.max(tri.v0).max(tri.v1).max(tri.v2);
        }

        return { min, max };
    }

    /**
     * Compute surface area of AABB
     */
    private surfaceArea(aabb: AABB): number {
        const d = new THREE.Vector3().subVectors(aabb.max, aabb.min);
        return 2 * (d.x * d.y + d.y * d.z + d.z * d.x);
    }

    /**
     * Recursive BVH build using SAH
     */
    private buildRecursive(
        triangles: BuildTriangle[],
        aabb: AABB,
        depth: number,
        parentObjectId: number
    ): number {
        const nodeIndex = this.nodes.length;

        // Create node
        const node: GPUBVHNode = {
            minX: aabb.min.x, minY: aabb.min.y, minZ: aabb.min.z, pad0: 0,
            maxX: aabb.max.x, maxY: aabb.max.y, maxZ: aabb.max.z, pad1: 0,
            leftChild: 0,
            rightChild: 0,
            skipPointer: 0, // Will be set later
            firstTriangle: 0,
            triangleCount: 0,
            flags: 0,
            objectId: parentObjectId,
            pad2: 0
        };
        this.nodes.push(node);

        // Track max depth
        this.stats.maxDepth = Math.max(this.stats.maxDepth, depth);

        // Make leaf if few triangles
        if (triangles.length <= this.maxLeafTriangles) {
            node.flags = 1; // isLeaf
            node.firstTriangle = this.triangleIndices.length;
            node.triangleCount = triangles.length;

            for (const tri of triangles) {
                this.triangleIndices.push(tri.originalIndex);
            }

            this.stats.leafCount++;
            return nodeIndex;
        }

        // Find best split using SAH
        const split = this.findBestSplit(triangles, aabb);

        if (!split) {
            // Couldn't find good split, make leaf
            node.flags = 1;
            node.firstTriangle = this.triangleIndices.length;
            node.triangleCount = triangles.length;

            for (const tri of triangles) {
                this.triangleIndices.push(tri.originalIndex);
            }

            this.stats.leafCount++;
            return nodeIndex;
        }

        // Partition triangles
        const leftTris: BuildTriangle[] = [];
        const rightTris: BuildTriangle[] = [];

        for (const tri of triangles) {
            if (tri.centroid.getComponent(split.axis) < split.position) {
                leftTris.push(tri);
            } else {
                rightTris.push(tri);
            }
        }

        // Handle degenerate cases
        if (leftTris.length === 0 || rightTris.length === 0) {
            // Split evenly
            const mid = Math.floor(triangles.length / 2);
            leftTris.length = 0;
            rightTris.length = 0;

            for (let i = 0; i < triangles.length; i++) {
                if (i < mid) {
                    leftTris.push(triangles[i]);
                } else {
                    rightTris.push(triangles[i]);
                }
            }
        }

        // Build children
        const leftAABB = this.computeAABB(leftTris);
        const rightAABB = this.computeAABB(rightTris);

        node.leftChild = this.buildRecursive(leftTris, leftAABB, depth + 1, parentObjectId);
        node.rightChild = this.buildRecursive(rightTris, rightAABB, depth + 1, parentObjectId);

        return nodeIndex;
    }

    /**
     * Find best split plane using SAH
     */
    private findBestSplit(
        triangles: BuildTriangle[],
        aabb: AABB
    ): { axis: number; position: number; cost: number } | null {
        const parentSA = this.surfaceArea(aabb);
        const parentCost = triangles.length; // Cost of not splitting

        let bestCost = parentCost;
        let bestAxis = -1;
        let bestPosition = 0;

        const extent = new THREE.Vector3().subVectors(aabb.max, aabb.min);

        // Try each axis
        for (let axis = 0; axis < 3; axis++) {
            if (extent.getComponent(axis) < 0.0001) continue;

            // Sort triangles by centroid on this axis
            const sortedTris = [...triangles];
            sortedTris.sort((a, b) =>
                a.centroid.getComponent(axis) - b.centroid.getComponent(axis)
            );

            // Sweep and compute SAH cost at each split position
            const binCount = Math.min(this.sahBinCount, triangles.length);
            const binSize = extent.getComponent(axis) / binCount;
            const axisMin = aabb.min.getComponent(axis);

            for (let i = 1; i < binCount; i++) {
                const splitPos = axisMin + i * binSize;

                // Count triangles on each side
                let leftCount = 0;
                let rightCount = 0;
                const leftAABB: AABB = {
                    min: new THREE.Vector3(Infinity, Infinity, Infinity),
                    max: new THREE.Vector3(-Infinity, -Infinity, -Infinity)
                };
                const rightAABB: AABB = {
                    min: new THREE.Vector3(Infinity, Infinity, Infinity),
                    max: new THREE.Vector3(-Infinity, -Infinity, -Infinity)
                };

                for (const tri of triangles) {
                    if (tri.centroid.getComponent(axis) < splitPos) {
                        leftCount++;
                        leftAABB.min.min(tri.v0).min(tri.v1).min(tri.v2);
                        leftAABB.max.max(tri.v0).max(tri.v1).max(tri.v2);
                    } else {
                        rightCount++;
                        rightAABB.min.min(tri.v0).min(tri.v1).min(tri.v2);
                        rightAABB.max.max(tri.v0).max(tri.v1).max(tri.v2);
                    }
                }

                if (leftCount === 0 || rightCount === 0) continue;

                // SAH cost
                const leftSA = this.surfaceArea(leftAABB);
                const rightSA = this.surfaceArea(rightAABB);
                const cost = 1 + (leftSA / parentSA) * leftCount + (rightSA / parentSA) * rightCount;

                if (cost < bestCost) {
                    bestCost = cost;
                    bestAxis = axis;
                    bestPosition = splitPos;
                }
            }
        }

        if (bestAxis === -1) {
            return null;
        }

        return { axis: bestAxis, position: bestPosition, cost: bestCost };
    }

    /**
     * Compute skip pointers for stackless traversal
     * Skip pointer = next sibling or ancestor's sibling
     */
    private computeSkipPointers(): void {
        const nodeCount = this.nodes.length;

        // Post-order traversal to set skip pointers
        const setSkipPointers = (nodeIdx: number, skipTo: number): void => {
            if (nodeIdx >= nodeCount) return;

            const node = this.nodes[nodeIdx];
            node.skipPointer = skipTo;

            const isLeaf = (node.flags & 1) === 1;

            if (!isLeaf && node.leftChild > 0 && node.rightChild > 0) {
                // Left child skips to right child
                setSkipPointers(node.leftChild, node.rightChild);
                // Right child skips to parent's skip
                setSkipPointers(node.rightChild, skipTo);
            }
        };

        // Root node skip pointer = end of tree (terminates traversal)
        setSkipPointers(0, nodeCount);
    }

    /**
     * Pack triangles in BVH order
     */
    private packTriangles(buildTriangles: BuildTriangle[]): void {
        this.triangles = [];

        for (const idx of this.triangleIndices) {
            const bt = buildTriangles[idx];
            this.triangles.push({
                v0x: bt.v0.x, v0y: bt.v0.y, v0z: bt.v0.z, pad0: 0,
                v1x: bt.v1.x, v1y: bt.v1.y, v1z: bt.v1.z, pad1: 0,
                v2x: bt.v2.x, v2y: bt.v2.y, v2z: bt.v2.z, pad2: 0,
                objectId: bt.objectId,
                triangleId: bt.triangleId,
                materialId: bt.materialId,
                pad3: 0
            });
        }
    }
}
